/**
 * Adzuki.TableExport (MAXX-174)
 *
 * Puts an "Export CSV" button under the tables in a chat answer, so the data
 * can be taken to Excel or Sheets. Shared by Ask Maxx, Maxx Intelligence and
 * Home Office Chat with Data: each calls enhance() on an answer once it has
 * finished, never while it is still streaming, so a half-built table does not
 * get a button.
 *
 *   Adzuki.TableExport.enhance(container, {prefix: 'ask-maxx', wrapper: '.chatbot-table-wrapper'});
 *   Adzuki.TableExport.cleanHtml($bubble)   // the answer's HTML without the buttons
 *
 * enhance() is safe to call again on the same answer: a table that already has
 * a button is skipped. The CSV is built when the button is clicked, so links
 * added to the cells after the answer rendered are exported too.
 *
 * Not Adzuki.TableCsvExport: that one doubles only the first quote mark in a
 * cell and drops "--", and it is built for the Home Office itinerary forms.
 */

(function TableExport(Adzuki)
{
	'use strict';

	var CONTROL_CLASS = 'adz-table-export',
		DONE_ATTR     = 'data-adz-table-export';

	// A container may arrive as a DOM node or a jQuery object.
	function toElement(target)
	{
		if (!target) {
			return null;
		}

		if (target.jquery) {
			return target.length ? target[0] : null;
		}

		return target;
	}

	// The cell as it reads on screen: line breaks become spaces and runs of
	// whitespace collapse, so a wrapped cell is still one CSV field. A link
	// exports as its visible text.
	function cellText(cell)
	{
		var clone = cell.cloneNode(true),
			breaks = clone.querySelectorAll('br'),
			i;

		for (i = 0; i < breaks.length; i++) {
			breaks[i].parentNode.replaceChild(document.createTextNode(' '), breaks[i]);
		}

		return (clone.textContent || '').replace(/\s+/g, ' ').trim();
	}

	// Every field quoted, quotes doubled. A value that Excel would read as a
	// formula (=, @, or + / - before something that is not a number) gets a
	// leading apostrophe, so a cell like "=HYPERLINK(...)" from the model stays
	// text. Negative numbers and phone numbers ("+1 800 ...", "+44 (20)") are
	// left alone.
	function csvField(value)
	{
		var text = String(value);

		if ((/^[=@\t\r]/).test(text) || (/^[-+][^\d.\s(]/).test(text)) {
			text = "'" + text;
		}

		return '"' + text.replace(/"/g, '""') + '"';
	}

	function tableToCsv(table)
	{
		var lines = [],
			rows  = table.querySelectorAll('tr'),
			fields,
			cells,
			span,
			r,
			c,
			s;

		for (r = 0; r < rows.length; r++) {
			fields = [];
			cells  = rows[r].cells;

			for (c = 0; c < cells.length; c++) {
				fields.push(csvField(cellText(cells[c])));

				// A cell spanning columns keeps the ones after it in place.
				span = parseInt(cells[c].getAttribute('colspan'), 10) || 1;
				for (s = 1; s < span; s++) {
					fields.push('""');
				}
			}

			if (fields.length) {
				lines.push(fields.join(','));
			}
		}

		return lines.join('\r\n');
	}

	// ask-maxx-2026-10-13.csv, or ask-maxx-2026-10-13-table-2.csv when an
	// answer has more than one table. The date is the advisor's own.
	function fileName(prefix, index, count)
	{
		var now  = new Date(),
			date = now.getFullYear()
				+ '-' + ('0' + (now.getMonth() + 1)).slice(-2)
				+ '-' + ('0' + now.getDate()).slice(-2);

		return prefix + '-' + date + (count > 1 ? '-table-' + (index + 1) : '') + '.csv';
	}

	// The byte-order mark tells Excel the file is UTF-8; without it accented
	// names and symbols open garbled.
	function download(csv, name)
	{
		var blob = new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8'}),
			url  = URL.createObjectURL(blob),
			link = document.createElement('a');

		link.href          = url;
		link.download      = name;
		link.style.display = 'none';
		document.body.appendChild(link);
		link.click();

		setTimeout(function _cleanUp() {
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		}, 0);
	}

	function buildControl(table, name)
	{
		var control = document.createElement('div'),
			button  = document.createElement('button');

		control.className = CONTROL_CLASS;
		button.type       = 'button';
		button.className  = CONTROL_CLASS + '-btn';
		button.title      = 'Download this table as a CSV file';
		button.innerHTML  = '<i class="fas fa-file-csv" aria-hidden="true"></i> Export CSV';

		button.addEventListener('click', function _onExport(event) {
			event.preventDefault();
			event.stopPropagation();

			try {
				download(tableToCsv(table), name);
			} catch (err) {
				console.error('Table export failed', err);
				if (Adzuki.StatusMessage) {
					Adzuki.StatusMessage('Error', 'The table could not be exported.');
				}
			}
		});

		control.appendChild(button);
		return control;
	}

	/**
	 * Add an Export CSV button under each table in `container` that has none yet.
	 *
	 * @param {Element|jQuery} container  the finished answer
	 * @param {Object}         options    prefix:  file name start, e.g. 'ask-maxx'
	 *                                    wrapper: the scroll box a table sits in, if
	 *                                             any; the button goes under the box
	 *                                             so it does not scroll sideways
	 * @return {number} how many buttons were added
	 */
	function enhance(container, options)
	{
		var el     = toElement(container),
			opts   = options || {},
			prefix = opts.prefix || 'table',
			added  = 0,
			tables,
			table,
			anchor,
			i;

		if (!el || !el.querySelectorAll) {
			return 0;
		}

		tables = el.querySelectorAll('table');

		for (i = 0; i < tables.length; i++) {
			table = tables[i];

			if (table.hasAttribute(DONE_ATTR) || !table.querySelector('tr')) {
				continue;
			}

			anchor = table;
			if (opts.wrapper && table.parentElement && table.parentElement.matches(opts.wrapper)) {
				anchor = table.parentElement;
			}

			anchor.parentNode.insertBefore(buildControl(table, fileName(prefix, i, tables.length)), anchor.nextSibling);
			table.setAttribute(DONE_ATTR, '1');
			added += 1;
		}

		return added;
	}

	/**
	 * The container's HTML without the export buttons — for copying, saving to
	 * snippets or storing a conversation, none of which should carry them.
	 *
	 * @param {Element|jQuery} container
	 * @return {string}
	 */
	function cleanHtml(container)
	{
		var el = toElement(container),
			clone,
			controls,
			marked,
			i;

		if (!el) {
			return '';
		}

		clone    = el.cloneNode(true);
		controls = clone.querySelectorAll('.' + CONTROL_CLASS);
		for (i = 0; i < controls.length; i++) {
			controls[i].parentNode.removeChild(controls[i]);
		}

		marked = clone.querySelectorAll('[' + DONE_ATTR + ']');
		for (i = 0; i < marked.length; i++) {
			marked[i].removeAttribute(DONE_ATTR);
		}

		return clone.innerHTML;
	}

	Adzuki.TableExport = {
		enhance  : enhance,
		cleanHtml: cleanHtml,
		toCsv    : tableToCsv,
	};
}(window.Adzuki || {}));
